import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { ProductRestrictions } from 'base/product_restrictions';
import { randomUUID } from 'base/uuid';
import {
  getParentSlotOfElement,
  isElementSelectable,
} from 'blocks/component_renderer/slots';
import { recordMap } from 'blocks/woot_woot/string_map';
import { computedFn } from 'mobx-utils';
import {
  anElementInstance,
  ElementInstance,
  ElementInstanceState,
  Page,
  pageDomain,
  PageState,
  Project,
  ProjectState,
  Section,
  SectionRef,
  SectionRefState,
  SectionState,
  Space,
  spaceDomain,
  SpaceState,
} from './model';

/** Map of subPage -> parent page or space */
export const parentsMap = computedFn(
  (project: Project): ReadonlyMap<Page, Page | Space> => {
    const output = new Map<Page, Page | Space>();
    function walk(parent: Page | Space) {
      for (const child of parent.subPages) {
        output.set(child, parent);
        walk(child);
      }
    }
    walk(project.homePage);
    for (const space of project.spaces) walk(space);
    return output;
  },
);

export function findParentSpace(
  project: Project,
  page: Page,
): Space | undefined {
  const parents = parentsMap(project);
  let at = parents.get(page);
  while (at) {
    if (spaceDomain.isMutable(at)) return at;
    at = parents.get(at);
  }
}

export function isChildInclusive(
  project: Project,
  parent: Page | Space,
  child: Page,
) {
  const parents = parentsMap(project);
  let at: Page | Space | undefined = child;
  while (true) {
    if (at === parent) return true;
    if (at == null || spaceDomain.isMutable(at)) return false;
    at = parents.get(at);
  }
}

export function pageAndAllSubpages(at: Page | Space): Page[] {
  const out: Page[] = [];
  if (pageDomain.isMutable(at)) out.push(at);
  for (const subPage of at.subPages) out.push(...pageAndAllSubpages(subPage));
  return out;
}
export function allProjectPages(project: Project): Page[] {
  const out = pageAndAllSubpages(project.homePage);
  for (const space of project.spaces) out.push(...pageAndAllSubpages(space));
  return out;
}

export function unrefPresentSections(
  list: readonly SectionRef[],
  project: Project,
): Section[];
export function unrefPresentSections(
  list: readonly SectionRefState[],
  project: ProjectState,
): SectionState[];
export function unrefPresentSections(
  list: readonly (SectionRef | SectionRefState)[],
  project: Project | ProjectState,
) {
  return list
    .map((ref) => {
      if (ref.type === 'inline') return ref.value;
      return project.globalSections.get(ref.id);
    })
    .filter(exists);
}

export function unrefSection(
  sref: SectionRef,
  project: Project,
): Section | undefined;
export function unrefSection(
  sref: SectionRefState,
  project: ProjectState,
): SectionState | undefined;
export function unrefSection(
  sref: SectionRef | SectionRefState,
  project: Project | ProjectState,
) {
  if (sref.type === 'inline') return sref.value;
  return project.globalSections.get(sref.id);
}

export function allSlugsInElementTree(
  root: ElementInstance | ElementInstanceState,
): string[] {
  const slugs = new Set<string>();
  for (const el of elementTreeDfs(root)) {
    slugs.add(el.source.slug);
  }
  return [...slugs];
}

export function* elementTreeDfs<
  EI extends ElementInstance | ElementInstanceState,
>(root: EI, reverse: boolean = false): Iterable<EI> {
  yield root;
  for (const slot of root.slotOverrides.values()) {
    for (const item of reverse ? [...slot.items].reverse() : slot.items) {
      // Typescript infers "ElementInstace | ElementInstanceState" here ;(
      yield* elementTreeDfs(item as EI, reverse);
    }
  }
}

export function parentElementWithinRoot(
  element: ElementInstance,
  root: ElementInstance,
): ElementInstance | undefined {
  for (const slot of root.slotOverrides.values()) {
    for (const item of slot.items) {
      if (item === element) return root;
      const inner = parentElementWithinRoot(element, item);
      if (inner) return inner;
    }
  }
}

export const firstSelectableAncestorElementWithinRoot = (
  element: ElementInstance,
  root: ElementInstance,
  library: LibraryCache,
): ElementInstance | undefined => {
  // Gets list of ancestors, starting with the direct parent, and walking up the tree.
  const getAncestors = (el: ElementInstance): ElementInstance[] => {
    const parent = parentElementWithinRoot(el, root);
    if (!parent) return [];

    return [parent, ...getAncestors(parent)];
  };

  return getAncestors(element).find((el) =>
    isElementSelectable({
      element: el,
      parentSlotType: getParentSlotOfElement({
        rootElement: root,
        element: el,
        library,
      })?.type,
      library,
    }),
  );
};

// create a shallow copy of an element with a new id.
// useful when inserting an element/section/page with IDs already like from copy/pasting.
export function shallowCloneElementState(
  element: ElementInstanceState,
): ElementInstanceState {
  return {
    ...element,
    id: randomUUID(),
    slotOverrides: recordMap(
      Array.from(element.slotOverrides.entries()).map(([k, v]) => [
        k,
        {
          ...v,
          items: v.items.map(shallowCloneElementState),
        },
      ]),
    ),
  };
}

// create a shallow copy of an inline section and give all elements new ids
export function shallowCloneSectionRefState(
  section: SectionRefState,
): SectionRefState {
  if (section.type === 'inline') {
    return {
      ...section,
      value: {
        ...section.value,
        element: {
          ...section.value.element,
          ...(section.value.element.current
            ? {
                current: shallowCloneElementState(
                  section.value.element.current,
                ),
              }
            : undefined),
        },
      },
    };
  }
  return section;
}

// create a shallow copy of an page and give all elements new ids
export function shallowClonePageState(page: PageState): PageState {
  return {
    ...page,
    sections: page.sections.map(shallowCloneSectionRefState),
    subPages: page.subPages.map(shallowClonePageState),
  };
}

type Node<
  TProject extends Project | ProjectState,
  TPage extends Page | PageState = TProject extends Project ? Page : PageState,
  TSpace extends Space | SpaceState = TProject extends Project
    ? Space
    : SpaceState,
  TSection extends Section | SectionState = TProject extends Project
    ? Section
    : SectionState,
  TElement extends
    | ElementInstance
    | ElementInstanceState = TProject extends Project
    ? ElementInstance
    : ElementInstanceState,
  TSectionRef extends SectionRef | SectionRefState = TProject extends Project
    ? SectionRef
    : SectionRefState,
> =
  | {
      type: 'page';
      node: TPage;
      space: TSpace | undefined;
    }
  | {
      type: 'space';
      node: TSpace;
    }
  | {
      type: 'section';
      sref: TSectionRef;
      node: TSection;
    }
  | {
      type: 'element';
      node: TElement;
    };

type VisitReturn = void | false;
type Visit<TProject extends Project | ProjectState> = (
  node: Node<TProject>,
) => VisitReturn;

export const walkProject = (
  project: Project,
  visit: Visit<Project>,
  { visitElements = false }: { visitElements?: boolean } = {},
) => {
  const visitPage = (page: Page, space: Space | undefined): VisitReturn => {
    if (visit({ type: 'page', node: page, space }) === false) {
      return false;
    }

    for (const sectionRef of page.sections) {
      if (visitSectionRef(sectionRef)) {
        return false;
      }
    }

    for (const subPage of page.subPages) {
      if (visitPage(subPage, space) === false) {
        return false;
      }
    }
  };

  const visitSectionRef = (sectionRef: SectionRef): VisitReturn => {
    const section = unrefSection(sectionRef, project);
    if (section == null) {
      return;
    }

    if (visit({ type: 'section', node: section, sref: sectionRef }) === false) {
      return false;
    }

    const root = section.element.current;
    if (root == null) {
      return;
    }

    if (visitElements) {
      for (const element of elementTreeDfs(root)) {
        if (visit({ type: 'element', node: element }) === false) {
          return false;
        }
      }
    }
  };

  const visitSpace = (space: Space): VisitReturn => {
    if (visit({ type: 'space', node: space }) === false) {
      return false;
    }

    for (const subPage of space.subPages) {
      if (visitPage(subPage, space) === false) {
        return false;
      }
    }
  };

  if (visitPage(project.homePage, undefined) === false) {
    return;
  }

  for (const space of project.spaces) {
    if (visitSpace(space) === false) {
      return;
    }
  }
};

export function getAllSections(project: Project) {
  const result: Map<
    string,
    { elementInstance: ElementInstance; sectionRef: SectionRef }
  > = new Map();
  walkProject(project, (nodeWrapper) => {
    if (nodeWrapper.type !== 'section') return;
    const node = nodeWrapper.node;
    const elementInstance = node.element.current;
    if (!elementInstance) return;
    const slug = elementInstance.source.slug;
    if (!slug) return;

    result.set(slug, { elementInstance, sectionRef: nodeWrapper.sref });
  });
  return result;
}

/**
 * Duplicate of walkProject but using ProjectState instead of Project
 *
 * I attempted to make it one function but the types didn't work and were monstrous.
 */
export const walkProjectState = (
  project: ProjectState,
  visit: Visit<ProjectState>,
) => {
  const visitPage = (
    page: PageState,
    space: SpaceState | undefined,
  ): VisitReturn => {
    if (visit({ type: 'page', node: page, space }) === false) {
      return false;
    }

    for (const sectionRef of page.sections) {
      if (visitSectionRef(sectionRef)) {
        return false;
      }
    }

    for (const subPage of page.subPages) {
      if (visitPage(subPage, space) === false) {
        return false;
      }
    }
  };

  const visitSectionRef = (sectionRef: SectionRefState): VisitReturn => {
    const section = unrefSection(sectionRef, project);
    if (section == null) {
      return;
    }

    if (visit({ type: 'section', node: section, sref: sectionRef }) === false) {
      return false;
    }

    const root = section.element.current;
    if (root == null) {
      return;
    }

    for (const element of elementTreeDfs(root)) {
      if (visit({ type: 'element', node: element }) === false) {
        return false;
      }
    }
  };

  const visitSpace = (space: SpaceState): VisitReturn => {
    if (visit({ type: 'space', node: space }) === false) {
      return false;
    }

    for (const subPage of space.subPages) {
      if (visitPage(subPage, space) === false) {
        return false;
      }
    }
  };

  if (visitPage(project.homePage, undefined) === false) {
    return;
  }

  for (const space of project.spaces) {
    if (visitSpace(space) === false) {
      return;
    }
  }
};

export function canViewWireframe(
  productRestrictions: ProductRestrictions,
  project: Project,
  page: Page,
) {
  // No restriction, all wireframes are visible.
  if (productRestrictions.pageLimit == null) {
    return true;
  }
  return getVisiblePages(productRestrictions, project).includes(page);
}

/**
 * Returns an array of visible pages in the project based off the product restrictions
 */
export function getVisiblePages(
  productRestrictions: ProductRestrictions,
  project: Project,
): Page[] {
  // Special case for one page limit – technically, they could have generated
  // another page but we only want to show them the home page.
  if (productRestrictions.pageLimit === 1) {
    return [project.homePage];
  }

  // Extract what generated pages there are
  const pages: { page: Page; generated: boolean }[] = [];
  walkProject(project, ({ type, node }) => {
    if (type !== 'page') {
      return;
    }

    // Only count nodes with generated content
    const generated = [...node.sections].some(
      (section) =>
        section.type == 'inline' && section.value.element.current != null,
    );

    pages.push({ page: node, generated });
  });

  const generatedPages = pages
    .filter((x) => x.generated)
    .map(({ page }) => page);

  // No restriction, all pages are visible
  if (productRestrictions.pageLimit == null) {
    return pages.map(({ page }) => page);
  }

  // If there are less than $pageLimit generated pages, only show the first $pageLimit pages
  if (generatedPages.length < productRestrictions.pageLimit) {
    return pages
      .slice(0, productRestrictions.pageLimit)
      .map(({ page }) => page);
  }

  // Otherwise, only show the first $pageLimit generated pages
  return generatedPages.slice(0, productRestrictions.pageLimit);
}

/**
 * Returns an array of visible pages in the project based off the product restrictions
 */
export function getVisiblePagesFromProjectState(
  productRestrictions: ProductRestrictions,
  project: ProjectState,
): PageState[] {
  // Special case for one page limit – technically, they could have generated
  // another page but we only want to show them the home page.
  if (productRestrictions.pageLimit === 1) {
    return [project.homePage];
  }

  // Extract what generated pages there are
  const pages: { page: PageState; generated: boolean }[] = [];
  walkProjectState(project, ({ type, node }) => {
    if (type !== 'page') {
      return;
    }

    // Only count nodes with generated content
    const generated =
      [...node.sections].some(
        (section) =>
          section.type == 'inline' && section.value.element.current != null,
      ) && node.pageType !== 'path';

    pages.push({ page: node, generated });
  });

  const generatedPages = pages
    .filter((x) => x.generated)
    .map(({ page }) => page);

  // No restriction, all pages are visible
  if (productRestrictions.pageLimit == null) {
    return pages.map(({ page }) => page);
  }

  // If there are less than $pageLimit generated pages, only show the first $pageLimit pages
  if (generatedPages.length < productRestrictions.pageLimit) {
    return pages
      .slice(0, productRestrictions.pageLimit)
      .map(({ page }) => page);
  }

  // Otherwise, only show the first $pageLimit generated pages
  return generatedPages.slice(0, productRestrictions.pageLimit);
}

// Clones an element while preserving its source, margin, and slot overrides.
// That is, most things except for text overrides.
export const cloneElementPreservingSlotOverrides = (
  el: ElementInstanceState,
): ElementInstanceState =>
  anElementInstance({
    source: el.source,
    margin: el.margin,
    slotOverrides: recordMap(
      [...el.slotOverrides].map(([slotId, slotOverride]) => [
        slotId,
        {
          items: slotOverride.items.map((item) =>
            cloneElementPreservingSlotOverrides(item),
          ),
        },
      ]),
    ),
  });

// Ensures that component JSONs for all elements in the page are fetched by the
// library cache.
export const prefetchComponents = async (
  page: PageState,
  library: LibraryCache,
  project: ProjectState,
) => {
  const { sections } = page;

  const slugs = sections
    .flatMap((sref) => {
      const section =
        sref.type === 'inline'
          ? sref.value
          : project.globalSections.get(sref.id);

      if (section?.element.current == null) return;

      return allSlugsInElementTree(section.element.current);
    })
    .filter(exists);

  await Promise.all(slugs.map((slug) => library.getPublicComponentJson(slug)));
};
